import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import type { LLMConfiguration } from '../config/llmProviders'
import type { Json } from '../types/supabase'
import { LLM_PROVIDERS } from '../config/llmProviders'
import { supabase } from '../lib/supabase'
import { useUser } from './UserContext'
import { ModelRouter } from '../services/modelRouter'
import { UserModelsService } from '../services/userModelsService'

interface LLMContextType {
    config: LLMConfiguration
    updateConfig: (newConfig: LLMConfiguration) => Promise<void>
    sendMessage: (messages: Array<{ role: string; content: string }>, options?: { agentId?: string }) => Promise<Response | { content: string }>
}

const LLMContext = createContext<LLMContextType | undefined>(undefined)

export function LLMProvider({ children }: { children: React.ReactNode }) {
    const { user } = useUser()

    // Initialize services
    const modelRouter = useMemo(() => new ModelRouter(), [])
    const userModelsService = useMemo(() => new UserModelsService(), [])

    const [config, setConfig] = useState<LLMConfiguration>({
        providerId: 'auto', // Default to Dynamic Router
        apiKey: '',
        modelId: 'auto',
        parameters: {
            temperature: 1,
            max_tokens: 4096,
            top_p: 1,
            stream: true
        }
    })

    // Load config from Supabase
    useEffect(() => {
        const loadConfig = async () => {
            if (!user) {
                console.log('[LLMContext] No user, skipping config load');
                return;
            }

            console.log('[LLMContext] Loading config for user:', user.id);
            const { data } = await supabase
                .from('llm_configurations')
                .select('*')
                .eq('user_id', user.id)
                .maybeSingle()

            if (data) {
                console.log('[LLMContext] Config loaded from DB:', {
                    provider: data.provider_id,
                    model: data.model_id,
                    hasApiKey: !!data.api_key_encrypted
                });
                setConfig({
                    providerId: data.provider_id,
                    apiKey: data.api_key_encrypted || '',
                    modelId: data.model_id,
                    parameters: data.parameters as Record<string, unknown>
                })
            } else {
                console.log('[LLMContext] No config found in DB');
            }
        }

        loadConfig()
    }, [user])

    const updateConfig = useCallback(async (newConfig: LLMConfiguration) => {
        console.log('[LLMContext] Updating config:', {
            provider: newConfig.providerId,
            model: newConfig.modelId,
            hasApiKey: !!newConfig.apiKey
        });
        setConfig(newConfig)

        if (user) {
            console.log('[LLMContext] Saving to DB for user:', user.id);
            const { error } = await supabase
                .from('llm_configurations')
                .upsert({
                    user_id: user.id,
                    provider_id: newConfig.providerId,
                    model_id: newConfig.modelId,
                    api_key_encrypted: newConfig.apiKey,
                    parameters: newConfig.parameters as unknown as Json
                }, { onConflict: 'user_id' })

            if (error) {
                console.error('[LLMContext] Error saving config to DB:', error);
                throw error;
            }

            // Sync with UserModelsService for Dynamic Router support
            // Only if we have a valid raw API key (starts with sk- for OpenAI, or generic check)
            // and a specific provider selected
            if (newConfig.apiKey && newConfig.providerId !== 'auto') {
                const provider = newConfig.providerId as 'openai' | 'anthropic' | 'google';
                // Simple check to avoid double encryption if we somehow have encrypted key in state
                // OpenAI keys start with sk-, Anthropic with sk-ant-, Google usually doesn't have a prefix but is long
                const isLikelyRawKey = newConfig.apiKey.startsWith('sk-') || newConfig.apiKey.length < 100;

                if (isLikelyRawKey) {
                    console.log('[LLMContext] Syncing key with UserModelsService for provider:', provider);
                    await userModelsService.activateProviderModels(
                        user.id,
                        provider,
                        newConfig.apiKey
                    ).catch(err => console.error('[LLMContext] Error syncing with UserModelsService:', err));
                }
            }

            console.log('[LLMContext] Config saved to DB');
        }
    }, [user, userModelsService])

    const sendMessage = useCallback(async (messages: Array<{ role: string; content: string }>, options?: { agentId?: string }) => {
        // 0. ORION BACKEND INTERCEPT (Native Agent Mode)
        if (options?.agentId) {
            console.log('[LLMContext] Using Orion Backend for Agent:', options.agentId);
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token;

            // Use relative path to let Vite proxy handle the target (Port 800/8000)
            const agentApiUrl = ''; // Relative path path prefix if needed, or empty for absolute relative to origin

            const response = await fetch(`${agentApiUrl}/api/v1/chat/completions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    agent_id: options.agentId,
                    messages,
                    stream: true
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `Backend Error: ${response.statusText}`);
            }

            return response;
        }

        // Check if using Dynamic Router (either 'auto' provider or OpenAI with 'auto' model)
        const useDynamicRouter = config.providerId === 'auto' || (config.providerId === 'openai' && config.modelId === 'auto');

        if (useDynamicRouter) {
            if (!user) {
                throw new Error('Você precisa estar logado para usar o Dynamic Router.')
            }

            // Get the last user message for complexity analysis
            const lastUserMessage = messages.filter(m => m.role === 'user').pop()
            if (!lastUserMessage) {
                throw new Error('Nenhuma mensagem do usuário encontrada.')
            }

            // Route to best model
            const routingResult = await modelRouter.routeMessage(user.id, lastUserMessage.content)
            console.log('[Dynamic Router] Selected:', routingResult.model.model, 'Reasoning:', routingResult.reasoning)

            // Get the decrypted API key for the selected provider
            const apiKey = await userModelsService.getDecryptedApiKey(user.id, routingResult.model.provider)
            if (!apiKey) {
                throw new Error(`API Key não encontrada para ${routingResult.model.provider}. Configure nas configurações de LLM.`)
            }

            // Find the provider config
            const provider = LLM_PROVIDERS.find(p => p.id === routingResult.model.provider)
            if (!provider) throw new Error('Provider not found')

            // Build request with selected model
            const headers: Record<string, string> = {
                'Content-Type': 'application/json'
            }

            if (provider.authType === 'bearer') {
                headers['Authorization'] = `Bearer ${apiKey}`
            } else if (provider.authType === 'header' && provider.authHeaderName) {
                headers[provider.authHeaderName] = apiKey
            }

            let endpoint = provider.endpoint
            if (provider.authType === 'query') {
                endpoint = `${endpoint}?key=${apiKey}`
            }

            let body: Record<string, unknown> = {
                model: routingResult.model.model,
                messages,
                temperature: config.parameters.temperature,
                max_tokens: routingResult.model.maxTokens,
                stream: config.parameters.stream
            }

            // Provider-specific adjustments
            if (routingResult.model.provider === 'anthropic') {
                body = {
                    model: routingResult.model.model,
                    messages: messages.filter(m => m.role !== 'system'),
                    system: messages.find(m => m.role === 'system')?.content,
                    max_tokens: routingResult.model.maxTokens,
                    temperature: config.parameters.temperature,
                    stream: config.parameters.stream
                }
            } else if (routingResult.model.provider === 'google') {
                endpoint = `${endpoint}/${routingResult.model.model}:generateContent?key=${apiKey}`
                body = {
                    contents: messages.map(m => ({
                        role: m.role === 'assistant' ? 'model' : 'user',
                        parts: [{ text: m.content }]
                    })),
                    generationConfig: {
                        temperature: config.parameters.temperature,
                        maxOutputTokens: routingResult.model.maxTokens
                    }
                }
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify(body)
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                throw new Error(errorData.error?.message || `API Error: ${response.statusText}`)
            }

            // Handle response formats
            if (routingResult.model.provider === 'google') {
                const data = await response.json()
                return {
                    content: data.candidates[0].content.parts[0].text,
                    role: 'assistant'
                }
            }

            if (!config.parameters.stream) {
                const data = await response.json()
                if (routingResult.model.provider === 'anthropic') {
                    return {
                        content: data.content[0].text,
                        role: 'assistant'
                    }
                }
                return data.choices[0].message
            }

            return response
        }

        // Standard provider flow (non-auto)
        const provider = LLM_PROVIDERS.find(p => p.id === config.providerId)
        if (!provider) throw new Error('Provider not found')

        const headers: Record<string, string> = {
            'Content-Type': 'application/json'
        }

        // Add authentication
        if (provider.authType === 'bearer') {
            headers['Authorization'] = `Bearer ${config.apiKey}`
        } else if (provider.authType === 'header' && provider.authHeaderName) {
            headers[provider.authHeaderName] = config.apiKey
        }

        let endpoint = provider.endpoint
        if (provider.authType === 'query') {
            endpoint = `${endpoint}?key=${config.apiKey}`
        }

        // Prepare body based on provider
        let body: Record<string, unknown> = {
            model: config.modelId,
            messages,
            ...config.parameters
        }

        // Provider specific adjustments
        if (config.providerId === 'anthropic') {
            body = {
                model: config.modelId,
                messages: messages.filter(m => m.role !== 'system'), // Claude handles system differently
                system: messages.find(m => m.role === 'system')?.content,
                max_tokens: config.parameters.max_tokens,
                temperature: config.parameters.temperature,
                stream: config.parameters.stream
            }
        } else if (config.providerId === 'google') {
            // Gemini format is different
            endpoint = `${endpoint}/${config.modelId}:generateContent?key=${config.apiKey}`
            body = {
                contents: messages.map(m => ({
                    role: m.role === 'assistant' ? 'model' : 'user',
                    parts: [{ text: m.content }]
                })),
                generationConfig: {
                    temperature: config.parameters.temperature,
                    maxOutputTokens: config.parameters.maxOutputTokens,
                    topP: config.parameters.topP,
                    topK: config.parameters.topK
                }
            }
        }

        const response = await fetch(endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(body)
        })

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            throw new Error(errorData.error?.message || `API Error: ${response.statusText}`)
        }

        // Handle different response formats
        if (config.providerId === 'google') {
            const data = await response.json()
            return {
                content: data.candidates[0].content.parts[0].text,
                role: 'assistant'
            }
        }

        if (!config.parameters.stream) {
            const data = await response.json()
            if (config.providerId === 'anthropic') {
                return {
                    content: data.content[0].text,
                    role: 'assistant'
                }
            }
            return data.choices[0].message
        }

        return response
    }, [config, user, modelRouter, userModelsService])

    return (
        <LLMContext.Provider value={{ config, updateConfig, sendMessage }}>
            {children}
        </LLMContext.Provider>
    )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLLMContext() {
    const context = useContext(LLMContext)
    if (context === undefined) {
        throw new Error('useLLMContext must be used within a LLMProvider')
    }
    return context
}
